/**
 * Advertorial Blog V2 - JS (corrigido)
 * - Add to cart via /cart/add.js
 * - Re-render de sections com Section Rendering API
 * - Abre o cart drawer do tema (Dawn e similares)
 * - Atualiza contador/bolha do carrinho
 *
 * Requisitos:
 *  - As sections "cart-drawer" e "cart-icon-bubble" devem existir no tema (Dawn padrão)
 *  - O wrapper de section padrão do Shopify existe como: #shopify-section-<sectionId>
 *  - Se seu tema usa outros IDs, ajuste 'SECTIONS_TO_RENDER'
 */

class AdvertorialBlogV2 {
  constructor() {
    this.SECTIONS_TO_RENDER = [
      "cart-drawer", // drawer deslizante
      "cart-icon-bubble", // contador/bolha da header
    ];
    this.init();
  }

  init() {
    this.setupScrollSpy();
    this.setupSmoothScrolling();
    this.setupProductForms();
    this.setupStickyCTA();
    this.setupAccessibility();
    this.setupMobileTOC();
    this.setupVideoControls();

    // Verifica se precisa abrir cart após refresh
    this.checkAndOpenCartAfterRefresh();
  }

  // ------------------------------
  // CART AFTER REFRESH
  // ------------------------------

  checkAndOpenCartAfterRefresh() {
    // Verifica se foi um refresh após adicionar ao cart
    const cartAdded = sessionStorage.getItem("advertorial_cart_added");
    const productTitle = sessionStorage.getItem("advertorial_product_title");

    if (cartAdded === "true") {
      console.log("Detected refresh after adding to cart, opening drawer...");

      // Limpa a flag para não abrir novamente
      sessionStorage.removeItem("advertorial_cart_added");
      sessionStorage.removeItem("advertorial_product_title");

      // Função para abrir cart e mostrar notificação
      const openCartAndNotify = () => {
        this.openCartDrawerIntelligent();
        if (productTitle) {
          this.showNotification(`${productTitle} added to cart!`, "success");
        }
      };

      // Tenta abrir imediatamente se a página já estiver pronta
      if (document.readyState === "complete") {
        openCartAndNotify();
      } else if (document.readyState === "interactive") {
        // Página carregou mas ainda não executou scripts
        setTimeout(openCartAndNotify, 100);
      } else {
        // Aguarda DOM estar pronto
        document.addEventListener("DOMContentLoaded", openCartAndNotify, { once: true });

        // Fallback com timeout menor
        setTimeout(openCartAndNotify, 300);
      }
    }
  }

  openCartDrawerIntelligent() {
    console.log("Opening cart drawer intelligently after refresh...");

    // Método 1: Dawn theme - cart-drawer element
    const dawnCartDrawer = document.querySelector("cart-drawer");
    if (dawnCartDrawer && typeof dawnCartDrawer.open === "function") {
      console.log("Using Dawn theme cart-drawer.open()");
      dawnCartDrawer.open();
      return;
    }

    // Método 2: Shopify's official cart drawer API
    if (window.Shopify?.cartDrawer?.open) {
      console.log("Using Shopify.cartDrawer.open()");
      window.Shopify.cartDrawer.open();
      return;
    }

    // Método 3: Try window.showCart function
    if (typeof window.showCart === "function") {
      console.log("Using window.showCart function");
      window.showCart();
      return;
    }

    // Método 4: Try custom cart drawer elements
    const cartDrawerSelectors = ["cart-drawer", "[data-cart-drawer]", ".cart-drawer", ".drawer", "[data-drawer]", "#CartDrawer"];

    for (const selector of cartDrawerSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        const methods = ["open", "show", "toggle"];
        for (const method of methods) {
          if (typeof element[method] === "function") {
            console.log(`Using ${selector}.${method}()`);
            element[method]();
            return;
          }
        }
      }
    }

    // Método 5: Try dispatching cart open events
    const cartEvents = ["cart:open", "cart:show", "drawer:open", "drawer:show", "cart-drawer:open", "cart-drawer:show"];

    for (const eventName of cartEvents) {
      console.log(`Dispatching event ${eventName}`);
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { source: "advertorial", afterRefresh: true },
        })
      );
    }

    // Método 6: Try clicking cart icon/button
    const cartClickSelectors = [
      'a[href*="cart"]',
      'button[class*="cart"]',
      "[data-cart]",
      '[aria-label*="cart" i]',
      ".cart-icon",
      ".cart-trigger",
      "[data-cart-trigger]",
    ];

    for (const selector of cartClickSelectors) {
      const element = document.querySelector(selector);
      if (element && element.offsetParent !== null) {
        console.log(`Clicking cart element: ${selector}`);
        element.click();
        return;
      }
    }

    console.log("Could not open cart drawer automatically");
  }

  // ------------------------------
  // TOOLS
  // ------------------------------

  log(...args) {
    // comente esta linha se quiser silenciar logs
    console.log("[Advertorial]", ...args);
  }

  money(cents) {
    // tenta usar o formatador do tema/Shopify; senão, fallback simples
    try {
      const moneyFormat = (window.Shopify && window.Shopify.money_format) || (window.theme && window.theme.moneyFormat) || "{{amount}}";

      if (window.Shopify && typeof Shopify.formatMoney === "function") {
        return Shopify.formatMoney(cents, moneyFormat);
      }
      const v = (cents / 100).toFixed(2);
      return moneyFormat.replace("{{ amount }}", v).replace("{{amount}}", v).replace("${{amount}}", `$${v}`);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /**
   * Troca a section ENTREGUE pela API pela section existente no DOM.
   * Tenta várias formas comuns de achar a section do tema.
   */
  swapSectionHTML(sectionId, html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html.trim();
    const fresh = wrapper.firstElementChild;
    if (!fresh) return false;

    // Alvos prováveis (ordem importa)
    const targets = [
      document.querySelector(`#shopify-section-${sectionId}`), // wrapper padrão do Shopify
      document.querySelector(`[data-section-id="${sectionId}"]`),
      document.querySelector(`[data-id="${sectionId}"]`),
      document.getElementById(sectionId),
      // elemento custom do Dawn:
      sectionId === "cart-drawer" ? document.querySelector("cart-drawer") : null,
    ].filter(Boolean);

    for (const target of targets) {
      target.replaceWith(fresh);
      return true;
    }

    this.log("Section alvo não encontrada no DOM:", sectionId);
    return false;
  }

  /**
   * Pede HTML das sections e substitui no DOM.
   * Use IDs reais do seu tema em this.SECTIONS_TO_RENDER.
   */
  async refreshCartSections() {
    if (!this.SECTIONS_TO_RENDER.length) return;

    const url = `/cart?sections=${encodeURIComponent(this.SECTIONS_TO_RENDER.join(","))}`;
    this.log("Fetching sections:", url);

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
    });
    if (!res.ok) throw new Error(`Sections HTTP ${res.status}`);

    const json = await res.json(); // { 'cart-drawer': '<html...>', 'cart-icon-bubble': '<html...>' }

    this.SECTIONS_TO_RENDER.forEach((id) => {
      if (json[id]) this.swapSectionHTML(id, json[id]);
    });

    document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { source: "advertorial" } }));
  }

  /**
   * Opens drawer using theme API
   */
  openCartDrawer() {
    // Dawn e derivadas
    const dawnDrawer = document.querySelector("cart-drawer");
    if (dawnDrawer && typeof dawnDrawer.open === "function") {
      dawnDrawer.open();
      return;
    }

    // API oficial presente em alguns temas/apps
    if (window.Shopify?.cartDrawer?.open) {
      window.Shopify.cartDrawer.open();
      return;
    }

    // Eventos genéricos
    document.dispatchEvent(new CustomEvent("cart:open", { detail: { source: "advertorial" } }));

    // último recurso: navegar para /cart
    // window.location.href = '/cart';
  }

  /**
   * Atualiza contadores simples com /cart.js (fallback)
   */
  async updateCartCountFallback() {
    try {
      const cart = await (await fetch("/cart.js")).json();
      const count = cart.item_count || 0;
      const candidates = document.querySelectorAll("[data-cart-count], .cart-count, .cart-icon-bubble, .bag-count");
      candidates.forEach((el) => {
        if ("textContent" in el) el.textContent = count;
      });
    } catch (e) {
      this.log("Failed updateCartCountFallback", e);
    }
  }

  // ------------------------------
  // ADD TO CART
  // ------------------------------

  setupProductForms() {
    const addToCartButtons = document.querySelectorAll(".advertorial-page .add-to-cart-btn");

    addToCartButtons.forEach((button) => {
      button.addEventListener("click", async (e) => {
        e.preventDefault();

        const variantId = button.dataset.variantId;
        const productTitle = button.dataset.productTitle;

        if (!variantId) {
          console.error("No variantId in button");
          return;
        }

        this.setButtonLoading(button, true);

        try {
          // 1) Adiciona ao carrinho
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `Error adding to cart (${response.status})`);
          }

          const data = await response.json();
          this.setButtonSuccess(button);
          this.showNotification(`${productTitle} added to cart!`, "success");

          // 2) Re-render das sections do carrinho (bloqueante)
          await this.refreshCartSections();

          // 3) Abre o drawer do tema
          this.openCartDrawer();

          // 4) Fallback do contador (se necessário)
          this.updateCartCountFallback();

          // 5) Evento global
          document.dispatchEvent(new CustomEvent("cart:updated", { detail: { item: data } }));

          // 6) Refresh rápido da página para garantir carrinho atualizado
          setTimeout(() => {
            // Marca que vamos fazer refresh para abrir cart depois
            sessionStorage.setItem("advertorial_cart_added", "true");
            sessionStorage.setItem("advertorial_product_title", productTitle);
            window.location.reload();
          }, 800);
        } catch (error) {
          console.error("Error adding to cart:", error);
          this.setButtonError(button);
          this.showNotification("Error adding to cart. Please try again.", "error");
        } finally {
          setTimeout(() => this.resetButton(button), 1800);
        }
      });
    });
  }

  // ------------------------------
  // BOTÃO: estados
  // ------------------------------

  setButtonLoading(button, isLoading) {
    const btnText = button.querySelector(".btn-text");
    const btnLoading = button.querySelector(".btn-loading");
    if (isLoading) {
      button.disabled = true;
      if (btnText) btnText.style.display = "none";
      if (btnLoading) btnLoading.style.display = "flex";
    } else {
      button.disabled = false;
      if (btnText) btnText.style.display = "block";
      if (btnLoading) btnLoading.style.display = "none";
    }
  }

  setButtonSuccess(button) {
    const btnText = button.querySelector(".btn-text");
    if (btnText) btnText.textContent = "Added! ✓";
    button.classList.add("success");
  }

  setButtonError(button) {
    const btnText = button.querySelector(".btn-text");
    if (btnText) btnText.textContent = "Error - Try Again";
    button.classList.remove("success");
    button.style.background = "#dc3545";
  }

  resetButton(button) {
    const btnText = button.querySelector(".btn-text");
    const btnLoading = button.querySelector(".btn-loading");
    button.disabled = false;
    button.classList.remove("success");
    button.style.background = "";
    if (btnText) {
      btnText.textContent = btnText.textContent
        .replace(" ✓", "")
        .replace("Added!", "Add to Cart – One-Time")
        .replace("Error - Try Again", "Add to Cart – One-Time");
      btnText.style.display = "block";
    }
    if (btnLoading) btnLoading.style.display = "none";
  }

  // ------------------------------
  // UI AUX
  // ------------------------------

  showNotification(message, type = "success") {
    const notification = document.createElement("div");
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 10000;
      background: ${type === "success" ? "#18a957" : "#dc3545"};
      color: #fff; padding: 12px 16px; border-radius: 8px;
      transform: translateX(120%); transition: transform .25s ease;
      box-shadow: 0 8px 24px rgba(0,0,0,.15);
    `;
    document.body.appendChild(notification);
    requestAnimationFrame(() => (notification.style.transform = "translateX(0)"));
    setTimeout(() => {
      notification.style.transform = "translateX(120%)";
      setTimeout(() => notification.remove(), 250);
    }, 2200);
  }

  // ------------------------------
  // As demais features do seu arquivo original
  // (mantidas e levemente enxugadas onde possível)
  // ------------------------------

  setupScrollSpy() {
    const sections = document.querySelectorAll(".advertorial-page main article section[id]");
    const links = document.querySelectorAll('.advertorial-page .toc a[href^="#"]');
    if (!sections.length || !links.length) return;

    const map = new Map();
    sections.forEach((s) => {
      const a = document.querySelector(`.advertorial-page .toc a[href="#${s.id}"]`);
      if (a) map.set(s.id, a);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        let active = null;
        let ratio = 0;
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > ratio) {
            ratio = e.intersectionRatio;
            active = e.target;
          }
        });
        if (active) {
          links.forEach((a) => a.classList.remove("active"));
          const link = map.get(active.id);
          if (link) link.classList.add("active");
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.1, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
  }

  setupSmoothScrolling() {
    const anchors = document.querySelectorAll('.advertorial-page a[href^="#"]');
    anchors.forEach((link) => {
      link.addEventListener("click", (e) => {
        const href = link.getAttribute("href");
        if (!href || href === "#") return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        const headerOffset = 100;
        const top = target.offsetTop - headerOffset;
        window.scrollTo({ top, behavior: "smooth" });
        history.pushState(null, "", href);
      });
    });
  }

  setupStickyCTA() {
    const stickyCTA = document.querySelector(".advertorial-page .sticky-cta");
    if (!stickyCTA) return;
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const shouldShow = y > 300;
      stickyCTA.style.transition = "transform .3s ease, opacity .3s ease";
      stickyCTA.style.transform = shouldShow ? "translateY(0)" : "translateY(100%)";
      stickyCTA.style.opacity = shouldShow ? "1" : "0";
      if (y < lastY && y > 100) {
        stickyCTA.style.transform = "translateY(100%)";
        stickyCTA.style.opacity = "0";
      }
      lastY = y;
      ticking = false;
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  setupAccessibility() {
    const skipLink = document.createElement("a");
    skipLink.href = "#main";
    skipLink.textContent = "Skip to main content";
    skipLink.className = "skip-link";
    skipLink.style.cssText =
      "position:absolute;top:-40px;left:6px;background:var(--brand-plum);color:#fff;padding:8px;border-radius:4px;z-index:1000;transition:top .3s";
    skipLink.addEventListener("focus", () => (skipLink.style.top = "6px"));
    skipLink.addEventListener("blur", () => (skipLink.style.top = "-40px"));
    document.body.insertBefore(skipLink, document.body.firstChild);

    document
      .querySelectorAll(".advertorial-page .btn:not([aria-label])")
      .forEach((b) => b.setAttribute("aria-label", b.textContent.trim()));
  }

  setupMobileTOC() {
    const tocToggle = document.querySelector(".toc-toggle");
    const toc = document.querySelector(".toc");
    if (!tocToggle || !toc) return;

    let open = false;
    tocToggle.addEventListener("click", () => {
      open = !open;
      toc.classList.toggle("show", open);
      tocToggle.textContent = open ? "✕" : "📋";
      tocToggle.setAttribute("aria-label", open ? "Close table of contents" : "Open table of contents");
    });

    toc.querySelectorAll("a").forEach((a) =>
      a.addEventListener("click", () => {
        setTimeout(() => {
          open = false;
          toc.classList.remove("show");
          tocToggle.textContent = "📋";
          tocToggle.setAttribute("aria-label", "Open table of contents");
        }, 300);
      })
    );

    const mq = window.matchMedia("(min-width: 1025px)");
    const onChange = (e) => {
      tocToggle.style.display = e.matches ? "none" : "flex";
      if (e.matches) {
        open = false;
        toc.classList.remove("show");
      }
    };
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    onChange(mq);
  }

  setupVideoControls() {
    const containers = document.querySelectorAll(".advertorial-page .video-container[data-video-id]");
    containers.forEach((container) => {
      const id = container.dataset.videoId;
      const video = document.getElementById(`video-${id}`);
      const controls = document.getElementById(`controls-${id}`);
      if (!video || !controls) return;

      const playBtn = controls.querySelector(".play-pause-btn");
      const playIcon = controls.querySelector(".play-icon");
      const pauseIcon = controls.querySelector(".pause-icon");
      const progressFill = controls.querySelector(".progress-fill");
      const progressHandle = controls.querySelector(".progress-handle");
      const progressContainer = controls.querySelector(".progress-container");
      const currentTimeEl = controls.querySelector(".current-time");
      const durationEl = controls.querySelector(".duration");
      const muteBtn = controls.querySelector(".mute-btn");
      const volumeIcon = controls.querySelector(".volume-icon");
      const muteIcon = controls.querySelector(".mute-icon");
      const fullscreenBtn = controls.querySelector(".fullscreen-btn");

      let isPlaying = false;
      let hideTimer;

      const fmt = (s) => {
        const m = Math.floor(s / 60);
        const ss = Math.floor(s % 60);
        return `${m}:${ss.toString().padStart(2, "0")}`;
      };

      const updateDur = () => {
        if (video.duration && !isNaN(video.duration)) durationEl.textContent = fmt(video.duration);
      };
      const updateProg = () => {
        if (!video.duration || isNaN(video.duration)) return;
        const p = (video.currentTime / video.duration) * 100;
        progressFill.style.width = `${p}%`;
        progressHandle.style.left = `${p}%`;
        currentTimeEl.textContent = fmt(video.currentTime);
      };

      playBtn.addEventListener("click", () => (isPlaying ? video.pause() : video.play()));
      video.addEventListener("click", () => (isPlaying ? video.pause() : video.play()));
      progressContainer.addEventListener("click", (e) => {
        if (!video.duration || isNaN(video.duration)) return;
        const rect = progressContainer.getBoundingClientRect();
        const pct = (e.clientX - rect.left) / rect.width;
        video.currentTime = pct * video.duration;
      });

      muteBtn.addEventListener("click", () => {
        video.muted = !video.muted;
        volumeIcon.style.display = video.muted ? "none" : "block";
        muteIcon.style.display = video.muted ? "block" : "none";
      });

      fullscreenBtn.addEventListener("click", () => video.requestFullscreen?.());

      video.addEventListener("loadedmetadata", updateDur);
      video.addEventListener("timeupdate", updateProg);
      video.addEventListener("play", () => {
        isPlaying = true;
        playIcon.style.display = "none";
        pauseIcon.style.display = "block";
        controls.classList.remove("hidden");
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => controls.classList.add("hidden"), 3000);
      });
      video.addEventListener("pause", () => {
        isPlaying = false;
        playIcon.style.display = "block";
        pauseIcon.style.display = "none";
        controls.classList.remove("hidden");
      });

      container.addEventListener("mouseenter", () => {
        controls.classList.remove("hidden");
        clearTimeout(hideTimer);
      });
      container.addEventListener("mouseleave", () => {
        if (isPlaying) {
          clearTimeout(hideTimer);
          hideTimer = setTimeout(() => controls.classList.add("hidden"), 300);
        }
      });

      video.load();
      setTimeout(() => {
        this.log("Video debug", {
          id,
          src: video.src || video.currentSrc,
          readyState: video.readyState,
          networkState: video.networkState,
        });
      }, 1500);
    });
  }
}

// Init
document.addEventListener("DOMContentLoaded", () => new AdvertorialBlogV2());

// Export
window.AdvertorialBlogV2 = AdvertorialBlogV2;
